import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Building2, CheckCircle, XCircle, Clock, Eye, ChevronRight,
  FileText, AlertTriangle, User, Mail, Globe, Calendar, Search,
  Download, MessageSquare, X, Send, Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const AdminCompanyVerifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [reviewingStep, setReviewingStep] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVerifications();
  }, [statusFilter]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await api.get(`/admin/company-approval/verifications${params}`);
      setVerifications(res.data);
    } catch (error) {
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (companyId) => {
    try {
      const res = await api.get(`/admin/company-approval/verifications/${companyId}`);
      setSelectedVerification(res.data);
    } catch (error) {
      toast.error('Failed to load verification details');
    }
  };

  const handleReviewStep = async (stepId, status) => {
    if (status === 'denied' && !adminNotes.trim()) {
      toast.error('Please provide feedback for denied steps');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/admin/company-approval/verifications/${selectedVerification.company_id}/steps/${stepId}`, {
        status,
        admin_notes: adminNotes
      });
      toast.success(`Step ${status === 'approved' ? 'approved' : 'denied'}`);
      setReviewingStep(null);
      setAdminNotes('');
      
      // Refresh the verification details
      handleViewDetails(selectedVerification.company_id);
      fetchVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update step');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      verified: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      denied: 'bg-red-100 text-red-700',
      not_started: 'bg-gray-100 text-gray-500'
    };
    const labels = {
      verified: 'Verified',
      pending: 'Pending Review',
      in_progress: 'In Progress',
      denied: 'Denied',
      not_started: 'Not Started'
    };
    
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.not_started}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getStepStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'submitted':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'denied':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const filteredVerifications = verifications.filter(v => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      v.company_name?.toLowerCase().includes(search) ||
      v.subdomain?.toLowerCase().includes(search) ||
      v.contact_email?.toLowerCase().includes(search)
    );
  });

  if (loading && !selectedVerification) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">Company Verifications</h1>
            </div>
            <p className="text-gray-500 text-sm">Review and approve company verification submissions.</p>
          </div>
          <Link
            to="/admin/company-approval-steps"
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium text-sm"
          >
            <FileText className="w-4 h-4" />
            Manage Steps
          </Link>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <FilterInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by company name, subdomain, or email..."
            className="flex-1 max-w-md"
          />
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'pending', label: 'Pending Review' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'verified', label: 'Verified' },
              { value: 'all', label: 'All' }
            ]}
          />
        </div>

        {/* Verifications List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {filteredVerifications.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Verifications Found</h3>
              <p className="text-gray-500">
                {statusFilter === 'pending' 
                  ? 'No companies pending verification review.'
                  : 'No verifications match your search criteria.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredVerifications.map((verification) => (
                <div 
                  key={verification.company_id} 
                  className="p-5 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewDetails(verification.company_id)}
                  data-testid={`verification-${verification.company_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{verification.company_name}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {verification.subdomain}.trados.app
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {verification.contact_email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-500 mb-1">
                        {verification.completed_steps}/{verification.total_steps} steps
                      </div>
                      {getStatusBadge(verification.status)}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedVerification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{selectedVerification.company_name}</h2>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{selectedVerification.subdomain}.trados.app</span>
                      <span>•</span>
                      <span>{selectedVerification.contact_email}</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedVerification(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Verification Progress</span>
                  <span className="text-sm text-gray-500">
                    {selectedVerification.steps?.filter(s => s.status === 'approved').length || 0}/{selectedVerification.steps?.length || 0} complete
                  </span>
                </div>
                <div className="flex gap-2">
                  {selectedVerification.steps?.map((step) => (
                    <div 
                      key={step.step_id}
                      className={`flex-1 h-2 rounded-full ${
                        step.status === 'approved' ? 'bg-green-500' :
                        step.status === 'submitted' ? 'bg-yellow-500' :
                        step.status === 'denied' ? 'bg-red-500' :
                        'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                {selectedVerification.steps?.map((step, index) => (
                  <div 
                    key={step.step_id} 
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    {/* Step Header */}
                    <div className="p-4 bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStepStatusIcon(step.status)}
                        <div>
                          <h4 className="font-medium text-gray-900">
                            Step {index + 1}: {step.name}
                          </h4>
                          {step.submitted_at && (
                            <span className="text-xs text-gray-500">
                              Submitted {new Date(step.submitted_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(step.status)}
                        {step.status === 'submitted' && (
                          <button
                            onClick={() => setReviewingStep(step.step_id)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                          >
                            Review
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Step Content - Questions & Answers */}
                    {step.questions && step.questions.length > 0 && (
                      <div className="p-4 space-y-4">
                        {step.questions.map((question) => (
                          <div key={question.id} className="border-l-2 border-gray-200 pl-4">
                            <p className="text-sm text-gray-500 mb-1" dangerouslySetInnerHTML={{ __html: question.question_text }} />
                            <p className="text-gray-900">
                              {question.field_type === 'file' && question.answer ? (
                                <a 
                                  href={question.answer} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  <Download className="w-4 h-4" />
                                  View Document
                                </a>
                              ) : Array.isArray(question.answer) ? (
                                question.answer.join(', ')
                              ) : (
                                question.answer || <span className="text-gray-400 italic">No answer provided</span>
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Admin Notes (if denied) */}
                    {step.status === 'denied' && step.admin_notes && (
                      <div className="p-4 bg-red-50 border-t border-red-100">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-red-700 text-sm">Revision Required</p>
                            <p className="text-sm text-red-600">{step.admin_notes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Review Panel */}
                    {reviewingStep === step.step_id && (
                      <div className="p-4 bg-blue-50 border-t border-blue-100">
                        <h5 className="font-medium text-blue-900 mb-3">Review This Step</h5>
                        <textarea
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Add feedback or notes (required for denial)..."
                          rows={3}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 mb-3"
                        />
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleReviewStep(step.step_id, 'approved')}
                            disabled={submitting}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleReviewStep(step.step_id, 'denied')}
                            disabled={submitting}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            <XCircle className="w-4 h-4" />
                            Request Revision
                          </button>
                          <button
                            onClick={() => {
                              setReviewingStep(null);
                              setAdminNotes('');
                            }}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default AdminCompanyVerifications;
